import React, { useState } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  TextField,
  Button,
  CardMedia,
  Typography,
  IconButton,
  InputAdornment,
  Container,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CardTermoDeUso from "../CardTermoDeUso";
import CardPoliticaPrivacidade from "../CardPoliticaPrivacidade";

interface LoginProps {
  onLogin: (role: "PARTNER" | "MASTER" | "ANALYST") => Promise<void>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);

  // Função de login
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      // Requisição de login
      const response = await fetch(API_URLS.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("E-mail ou senha inválidos.");
      }

      const data = await response.json();
      sessionStorage.setItem("token", data.access_token); // Armazenando o token no sessionStorage
      sessionStorage.setItem("userInfo", JSON.stringify(data.user)); // Armazenando os dados do usuário (se necessário)

      // Requisição para obter o perfil do usuário (role)
      const profileResponse = await fetch(API_URLS.role, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${data.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!profileResponse.ok) {
        throw new Error("Erro ao obter informações do perfil.");
      }

      const profileData = await profileResponse.json();
      sessionStorage.setItem("role", profileData.role); // Armazenando o role no sessionStorage
      await onLogin(profileData.role as "PARTNER" | "MASTER" | "ANALYST"); // Passando o role para o componente pai

      setError(""); // Limpar mensagem de erro em caso de sucesso
    } catch (error: any) {
      console.error("Erro durante o login:", error);
      setError(error.message || "Erro ao fazer login.");
    }
  };

  // Alternar a visibilidade da senha
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Abrir termos de uso
  const handleOpenTerms = () => {
    setShowTerms(true);
  };

  // Abrir política de privacidade
  const handleOpenPrivacy = () => {
    setShowPrivacy(true);
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 3,
          boxShadow: 3,
          borderRadius: 1,
          backgroundColor: "white",
        }}
      >
        <CardMedia
          component="img"
          image="/LOGO-COMPLETA.svg"
          alt="Logo Sauhde"
          sx={{ width: 150, height: "auto" }}
        />
        <Typography variant="h5" sx={{ marginTop: 2 }}>
          Login
        </Typography>

        {error && (
          <Typography color="error" variant="body2" sx={{ marginTop: 2 }}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit} style={{ width: "100%", marginTop: 3 }}>
          <TextField
            label="E-mail"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
          />
          <TextField
            label="Senha"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" align="center" sx={{ marginTop: 2 }}>
            Ao acessar a plataforma, você concorda com os{" "}
            <a href="#" onClick={handleOpenTerms}>
              termos de uso
            </a>{" "}
            e{" "}
            <a href="#" onClick={handleOpenPrivacy}>
              política de privacidade
            </a>
            .
          </Typography>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ marginTop: 3, padding: "12px" }}
          >
            Entrar
          </Button>
        </form>

        {showTerms && <CardTermoDeUso onClose={() => setShowTerms(false)} />}
        {showPrivacy && (
          <CardPoliticaPrivacidade onClose={() => setShowPrivacy(false)} />
        )}
      </Box>
    </Container>
  );
};

export default Login;
